// Copyright (c) 2026 AesirX.io Ltd. All rights reserved.
// This source code is proprietary and confidential.
// See LICENSE.md for licensing terms.
// ── Pack selector vocabulary (the ONLY selectors src may use) ──
/** The `register_surface` a fee-schedule template declares. */
export const REGULATORY_FEE_SCHEDULE_REGISTER_SURFACE = 'regulatory_fee_schedule';
/** The template fields the register reads, by role. */
export const REGULATORY_FEE_SCHEDULE_FIELDS = {
    /** One option per schedule row. */
    rows: 'schedule_row',
    /** One option per procedure an application can be for. */
    procedures: 'application_procedure',
    /** Collecting bodies the pack names, each dated and scoped to procedures. */
    collectingBodies: 'collecting_body_named',
};
/**
 * The option attributes of a schedule row, read off `option.raw` as authored
 * (AC2). An attribute that is absent or of the wrong type is REPORTED in
 * `DeclaredFeeRow.attribute_issues`, never coerced: `"8000000"` is not a
 * number and is not read as one.
 */
export const FEE_ROW_ATTRIBUTES = {
    schedulePart: 'schedule_part',
    scheduleRow: 'schedule_row',
    chargeKind: 'charge_kind',
    payerRule: 'payer_rule',
    amount: 'amount_vnd',
    amountUnit: 'amount_unit',
    procedureKind: 'procedure_kind',
    refundOnRefusal: 'refund_on_refusal',
    refundNote: 'refund_note',
    readingNote: 'reading_note',
    transitionalProvision: 'transitional_provision',
    sourceReference: 'source_reference',
};
/** The seven attributes AC2 names: each is read as declared or reported absent. */
export const FEE_ROW_REQUIRED_ATTRIBUTES = [
    FEE_ROW_ATTRIBUTES.schedulePart,
    FEE_ROW_ATTRIBUTES.scheduleRow,
    FEE_ROW_ATTRIBUTES.chargeKind,
    FEE_ROW_ATTRIBUTES.payerRule,
    FEE_ROW_ATTRIBUTES.amount,
    FEE_ROW_ATTRIBUTES.amountUnit,
    FEE_ROW_ATTRIBUTES.procedureKind,
];
/** The option attributes of a procedure. */
export const FEE_PROCEDURE_ATTRIBUTES = {
    /** Values of `schedule_row` options that price this procedure. */
    scheduleRows: 'schedule_rows',
    /** `true` when no row prices the procedure; `no_rate_note` says why. */
    noRateSet: 'no_rate_set',
    noRateNote: 'no_rate_note',
    registerSide: 'register_side',
    readingNote: 'reading_note',
    /** Why the pack declares no collecting body for this procedure. */
    collectingBodyOpenNote: 'collecting_body_open_note',
    sourceReference: 'source_reference',
};
/** The option attributes of a named collecting body. */
export const FEE_COLLECTING_BODY_ATTRIBUTES = {
    appliesToProcedures: 'applies_to_procedures',
    /** The window of the instrument that names the body, shown with its source. */
    instrumentWindowFrom: 'instrument_window_from',
    instrumentWindowUntil: 'instrument_window_until',
    sourceReference: 'source_reference',
    sourceNote: 'source_note',
};
/**
 * Who pays, and when, as a row declares it. `applicant_on_filing` is owed when
 * the dossier is filed; `certificate_grantee_no_moment_stated` is paid by the
 * person granted the certificate at a moment the instrument does not state.
 */
export const FEE_PAYER_RULES = [
    'applicant_on_filing',
    'certificate_grantee_no_moment_stated',
];
/** Which register a procedure's applications live on. */
export const FEE_REGISTER_SIDES = ['licence_application', 'sender_identity'];
/**
 * What a row declares about a refused application. The only value is a
 * statement about the instruments read, never "non-refundable".
 */
export const FEE_REFUND_ON_REFUSAL_VALUES = ['none_provided_by_instruments_read'];
/**
 * The date whose rates are offered and why (Slice B).
 *
 * - `filing_date` — the rows in force on the application's filing date.
 * - `transitional_provision` — the application was filed before the rows'
 *   commencement and is pending at commencement; the declaring row's
 *   transitional provision applies the rows in force on commencement.
 * - `no_row_in_force` — no row of the procedure is in force on the filing
 *   date and no transitional provision reaches it: no row is offered.
 * - `no_rate_set` — the pack declares that no row prices the procedure.
 * - `not_filed` — the application has no filing date, so no rate date exists.
 */
export const FEE_RATE_BASES = [
    'filing_date',
    'transitional_provision',
    'no_row_in_force',
    'no_rate_set',
    'not_filed',
];
// ── Applications (Slice A, Slice C) ──
export const FEE_APPLICATION_DECISIONS = ['pending', 'issued', 'refused'];
/** Why an application is on the completeness list. */
export const FEE_COMPLETENESS_GAPS = ['payment_record_missing'];
// ── Fee lines (Slice A, Slice D) ──
/** What a fee line belongs to. */
export const FEE_LINE_OWNER_TYPES = ['licence_application', 'sender_name_certificate'];
export const FEE_COLLECTING_BODY_SOURCES = ['pack', 'operator'];
/**
 * Whether a line was priced against its owner's procedure and filing date
 * (gate E12.5 pass 2): it carries a schedule row, or a collecting body the
 * installed schedule named for the procedure on the rate date. Only such a
 * line fixes the two: a line with no row and a typed body (a filing-date-less
 * or historic payment) was judged against nothing, so the procedure and the
 * filing date may still move. The server's lock and the screens' disabled
 * fields read this one predicate.
 */
export function feeLineFixesRateInputs(line) {
    return line.row !== null || line.collecting_body_source === 'pack';
}
// ── Collections, attachment owners, error codes ──
export const LICENCE_FEE_APPLICATION_COLLECTION = 'grc_licence_fee_applications';
export const FEE_LINE_COLLECTION = 'grc_fee_lines';
export const SENDER_NAME_CERTIFICATE_COLLECTION = 'grc_sender_name_certificates';
export const REGULATORY_FEE_ERROR_CODES = {
    /** A procedure, row or collecting body outside the installed vocabulary. */
    VOCABULARY_VALUE_UNKNOWN: 'VOCABULARY_VALUE_UNKNOWN',
    /** The row is not offered for this application's procedure on its rate date. */
    FEE_ROW_NOT_OFFERED: 'FEE_ROW_NOT_OFFERED',
    /** The procedure belongs to the other register side. */
    FEE_PROCEDURE_WRONG_SIDE: 'FEE_PROCEDURE_WRONG_SIDE',
    /** The named attachment is not held for this organisation. */
    RECEIPT_NOT_HELD: 'RECEIPT_NOT_HELD',
    /** The attachment exists but is bound to another line. */
    RECEIPT_BOUND_ELSEWHERE: 'RECEIPT_BOUND_ELSEWHERE',
    /** A stale save (the record moved since it was read). */
    STALE_WRITE: 'STALE_WRITE',
    /**
     * No template declaring the fee-schedule surface is operational for the
     * organisation (not installed, or installed for reference): nothing can be
     * recorded against a schedule that does not run. Carries the
     * `declaration_absence` in `details`.
     */
    FEE_SCHEDULE_NOT_OPERATIONAL: 'FEE_SCHEDULE_NOT_OPERATIONAL',
    /** A receipt cannot be named at create: the line it binds to does not exist yet. */
    RECEIPT_NOT_BINDABLE_AT_CREATE: 'RECEIPT_NOT_BINDABLE_AT_CREATE',
    /** Both a declared collecting body and a typed one were sent; a line names one. */
    COLLECTING_BODY_AMBIGUOUS: 'COLLECTING_BODY_AMBIGUOUS',
    /** A certificate still holds charge lines; delete them first. */
    CERTIFICATE_HAS_CHARGE_LINES: 'CERTIFICATE_HAS_CHARGE_LINES',
    /**
     * 409 — the procedure or filing date of an application or certificate cannot
     * change while it holds a PRICED fee line ({@link feeLineFixesRateInputs}):
     * that line's row, rate basis and rate date, or its collecting body named by
     * the schedule, were judged against them (gate E12.5 passes 1 and 2). A line
     * with no row and a typed body priced nothing and does not lock them.
     * Remove the row and the named body from those lines (or delete them) first,
     * or leave the two fields as they are. Carries `fee_line_count` (the priced
     * lines), `lines` (their ids) and `fields`.
     */
    FEE_LINES_RECORDED: 'FEE_LINES_RECORDED',
    /**
     * 409 — a line's paid date would fall before the commencement while another
     * line of the same application or certificate is priced under the
     * transitional provision (gate E12.5 pass 2). The provision reaches a DOSSIER
     * unpaid on the commencement, so a dossier with a payment before it is not
     * pending at commencement, and no line of it is priced under the provision.
     * Remove the row from those lines first, or keep the paid date. Carries
     * `lines` (the transitional lines' ids) and `commencement`.
     */
    TRANSITIONAL_LINES_RECORDED: 'TRANSITIONAL_LINES_RECORDED',
    /**
     * 400 — a decision recorded as `pending` names a decision date, a reference
     * or a document; a pending decision has none (gate E12.5 pass 1).
     */
    DECISION_PENDING_HAS_DETAILS: 'DECISION_PENDING_HAS_DETAILS',
    /**
     * 409 — a fee line that holds a receipt is not deleted: the receipt is
     * evidence (the certificate rule, `CERTIFICATE_HAS_CHARGE_LINES`, one level
     * down). Unbind the receipt first (`receipt_attachment_id: null`).
     */
    FEE_LINE_HAS_RECEIPT: 'FEE_LINE_HAS_RECEIPT',
    /** The application, certificate or fee line was not found for this organisation. */
    NOT_FOUND: 'FEE_RECORD_NOT_FOUND',
    /**
     * 422 — one export would carry more lines than
     * {@link REGULATORY_FEE_LINE_EXPORT_MAX_LINES}. Carries `line_count` and
     * `max_lines`; nothing is written.
     */
    EXPORT_TOO_LARGE: 'FEE_LINE_EXPORT_TOO_LARGE',
};
// ── Sprint 62.55.0 — the period view and the exports (UC-VN-109 F-1, F-3) ──
/**
 * A paid-on period: one calendar month, `YYYY-MM`. Finance books fees by
 * month; the period view and the period export read the lines whose paid date
 * falls in it. A line with no paid date is in no period.
 */
export const FEE_LINE_PERIOD_PATTERN = /^\d{4}-(0[1-9]|1[0-2])$/;
/** The first and the day after the last calendar date of a `YYYY-MM` period. */
export function feeLinePeriodBounds(period) {
    if (!FEE_LINE_PERIOD_PATTERN.test(period))
        return null;
    const year = Number(period.slice(0, 4));
    const month = Number(period.slice(5, 7));
    const nextYear = month === 12 ? year + 1 : year;
    const nextMonth = month === 12 ? 1 : month + 1;
    return {
        from: `${period}-01`,
        before: `${String(nextYear).padStart(4, '0')}-${String(nextMonth).padStart(2, '0')}-01`,
    };
}
export const REGULATORY_FEE_LINE_EXPORT_LOCALES = ['en', 'vi'];
/** The most lines one fee-line export carries; past it the export is refused (422). */
export const REGULATORY_FEE_LINE_EXPORT_MAX_LINES = 5000;
/** What one fee-line export covers. */
export const REGULATORY_FEE_LINE_EXPORT_SCOPES = ['owner', 'period'];
//# sourceMappingURL=regulatory-fees.js.map